import React from 'react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { Toggle } from '@qbe/ads-blocks-forms';
import type { ToggleProps } from '@qbe/ads-blocks-forms';
import { styled } from '@qbe/ads-core';

type ToggleControllerProps<T extends FieldValues> = {
  control: Control<T, any>;
  name: keyof FieldValues;
  options: { text: string; value: string }[];
  capture?: boolean;
  title: string;
  onAfterChange?: (event: React.FormEvent<HTMLFieldSetElement>) => void;
} & Omit<ToggleProps, 'optionList' | 'onChange' | 'helperText' | 'error'>;

const StyledToggle = styled(Toggle)`
  position: static;
`;

export function ToggleController<T extends FieldValues>({
  control,
  name,
  capture = false,
  options,
  onAfterChange,
  ...rest
}: ToggleControllerProps<T>) {
  return (
    <Controller
      name={name as Path<T>}
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <StyledToggle
          id={field.name}
          {...field}
          {...rest}
          optionList={options.map((option) => ({
            autoComplete: 'off',
            id: field.name + '__' + option.value,
            value: option.value,
            label: option.text ?? option.value,
            defaultChecked: field.value === option.value,
            'data-test': field.name,
            'data-track': field.name,
            'data-track-value': capture ? 'capture' : 'mask',
          }))}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            field.onChange(e.target.value);
            if (onAfterChange) {
              const fieldset = e.currentTarget.closest('fieldset') as HTMLFieldSetElement;
              if (fieldset) {
                const syntheticEvent = {
                  ...e,
                  currentTarget: fieldset,
                } as React.FormEvent<HTMLFieldSetElement>;
                onAfterChange(syntheticEvent);
              }
            }
          }}
          helperText={error?.message}
          error={!!error}
        />
      )}
    />
  );
}
